// One attach control for the whole app: tap to take a photo or pick a file, or drag
// and drop onto it. Accepts images AND PDFs by default, so an "Add Photo" spot also
// takes a scanned packing slip or a spec sheet without anyone thinking about it.
//
// Why one component: there were ~40 hand-rolled file inputs, each with its own rules —
// some took PDFs, most didn't, almost none took a drop. This makes every location
// behave the same, and any new one inherits it for free.
//
// It hands the parent an array of File objects via onFiles; the parent still owns what
// happens next (queue them, upload now — whatever it already did). On mobile there is
// deliberately no `capture` lock: the OS sheet then offers Camera, Photo Library AND
// Files, so a photo and a PDF are both one tap away. The server already stores whatever
// comes in, so nothing downstream needs to change.
import React, { useRef, useState } from 'react';

const ACCEPT = {
  both:  'image/*,application/pdf',
  image: 'image/*',
  pdf:   'application/pdf,.pdf',
  any:   '',               // anything the OS will hand over — PDFs, docs, drawings, images
};
// Keep a dropped file only if it matches what this spot takes — a stray .docx dragged
// onto a photo tile should be ignored, not uploaded. 'any' takes whatever is dropped.
const okFor = (kind, file) => {
  if (kind === 'any') return true;
  const t = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  const isPdf = t === 'application/pdf' || name.endsWith('.pdf');
  const isImg = t.startsWith('image/');
  if (kind === 'pdf') return isPdf;
  if (kind === 'image') return isImg;
  return isPdf || isImg;
};

export default function AttachButton({
  onFiles,                 // (File[]) => void
  multiple = false,
  accept = 'both',         // 'both' | 'image' | 'pdf'
  label,                   // defaults to a sensible photo/file wording
  icon = '📎',
  disabled = false,
  variant = 'dashed',      // 'dashed' | 'solid' | 'plain'
  style = {},
}) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const word = label || (accept === 'pdf' ? (multiple ? 'Add Files' : 'Add File')
    : accept === 'image' ? (multiple ? 'Add Photos' : 'Add Photo')
    : (multiple ? 'Add Photos or Files' : 'Add Photo or File'));

  const hand = list => {
    const files = Array.from(list || []).filter(f => okFor(accept, f));
    if (files.length) onFiles(multiple ? files : [files[0]]);
  };

  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '10px 14px', borderRadius: 10, fontSize: 14, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
    boxSizing: 'border-box', textAlign: 'center', transition: 'background .12s, border-color .12s',
    userSelect: 'none',
  };
  const look = variant === 'solid'
    ? { border: '1.5px solid #1e3a5f', background: over ? '#dbeafe' : '#1e3a5f', color: over ? '#1e3a5f' : '#fff' }
    : variant === 'plain'
      ? { border: '1px solid #e5e7eb', background: over ? '#eff6ff' : '#fff', color: '#374151' }
      : { border: `1.5px dashed ${over ? '#2563eb' : '#86efac'}`, background: over ? '#eff6ff' : '#f0fdf4', color: over ? '#1d4ed8' : '#15803d' };

  return (
    <div
      role="button" tabIndex={disabled ? -1 : 0}
      onClick={() => { if (!disabled && ref.current) ref.current.click(); }}
      onKeyDown={e => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); ref.current && ref.current.click(); } }}
      onDragOver={e => { if (disabled) return; e.preventDefault(); e.stopPropagation(); if (!over) setOver(true); }}
      onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setOver(false); }}
      onDrop={e => { if (disabled) return; e.preventDefault(); e.stopPropagation(); setOver(false); hand(e.dataTransfer?.files); }}
      title={`${word} — drag and drop works too`}
      style={{ ...base, ...look, ...style }}>
      <span style={{ fontSize: 16, lineHeight: 1 }}>{over ? '⬇' : icon}</span>
      <span>{over ? 'Drop to attach' : word}</span>
      <input ref={ref} type="file" accept={accept === 'any' ? undefined : (ACCEPT[accept] || ACCEPT.both)} multiple={multiple}
        disabled={disabled} style={{ display: 'none' }}
        onChange={e => { hand(e.target.files); e.target.value = ''; }} />
    </div>
  );
}

// Is this file (by name or mime) a PDF? Used by preview grids so a PDF shows a page
// tile instead of a broken <img>.
export const isPdf = f => {
  const s = String(typeof f === 'string' ? f : (f?.name || f?.original_name || f?.filename || f?.mime_type || '')).toLowerCase();
  return s.endsWith('.pdf') || s.includes('application/pdf');
};

// A uniform thumbnail: the image itself, or a 📄 tile that says PDF. `href` (optional)
// makes it open in a new tab. `size` is the square edge in px.
export function AttachThumb({ src, name, href, size = 70, onRemove }) {
  const pdf = isPdf(name) || isPdf(src);
  const box = { position: 'relative', width: size, height: size, borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden', flexShrink: 0 };
  const inner = pdf
    ? <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: '#fef2f2', color: '#b91c1c' }}>
        <span style={{ fontSize: size > 54 ? 22 : 16 }}>📄</span>
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5 }}>PDF</span>
      </div>
    : <img src={src} alt={name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
  return (
    <div style={box}>
      {href ? <a href={href} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}>{inner}</a> : inner}
      {onRemove && (
        <button type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
          style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 12, lineHeight: '18px', cursor: 'pointer', padding: 0 }}>×</button>
      )}
    </div>
  );
}
